import { Button } from "./ui/button.tsx";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ChevronRight,
  Sparkles,
  Code2,
  CheckCircle2,
  TrendingUp,
  Target,
  BarChart3,
  Mail,
  Briefcase,
  Award,
  LineChart,
  FileText,
  Users,
  Layers,
  Zap,
  Brain,
  GraduationCap
} from "lucide-react";
import { useState } from "react";
import PublicNavigation from "../pages/Landing/components/PublicNavigation";
import PublicFooter from "../pages/Landing/components/PublicFooter";
// import { useEffect } from "react";
// import axios from "axios";
// import { API_URLS } from "../config";

// interface UserCountResponse {
//   total_users: number;
//   remaining_slots: number;
//   max_slots: number;
// }

type Audience = 'candidates' | 'recruiters';

export default function LandingPage() {
  const { loginWithRedirect } = useAuth0();
  // const [userCount, setUserCount] = useState<UserCountResponse | null>(null);
  const [activeAudience, setActiveAudience] = useState<Audience>('candidates');

  // Fetch user count on component mount
  // useEffect(() => {
  //   const fetchUserCount = async () => {
  //     try {
  //       const response = await axios.get<UserCountResponse>(`${API_URLS.BASE}/auth/user-count`);
  //       setUserCount(response.data);
  //     } catch (error) {
  //       console.error('Error fetching user count:', error);
  //       // Set default values if fetch fails
  //       setUserCount({ total_users: 0, remaining_slots: 100, max_slots: 100 });
  //     }
  //   };

  //   fetchUserCount();
  // }, []);

  const handleGetStarted = () => {
    loginWithRedirect({
      authorizationParams: {
        audience: 'https://veltus.au.auth0.com/api/v2/',
        scope: 'openid profile email roles'
      }
    });
  };

  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <PublicNavigation
        showRecruiterLink
        onRecruiterClick={() => setActiveAudience('recruiters')}
      />
      <div className="pt-12" />

      {/* Beta Banner */}
      {/* {userCount && (
        <div className="fixed top-16 left-0 right-0 bg-amber-50 border-b border-amber-200 z-40">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center justify-center gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-amber-600" />
              <span className="font-medium text-amber-900">
                OnSite is free for the first 100 users during beta release.
              </span>
              <span className="font-semibold text-amber-700">
                {userCount.remaining_slots}/100 free profiles remaining
              </span>
            </div>
          </div>
        </div>
      )} */}

      {/* Dynamic Hero Section */}
      <AnimatePresence mode="wait">
        {activeAudience === 'candidates' ? (
          <motion.div
            key="candidates"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {/* Candidates Hero */}
            <section className="pt-8 pb-20 px-6">
              <div className="max-w-4xl mx-auto">
                <div className="text-center">
                  {/* Badge */}
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 mb-6">
                    <Zap className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      Dynamic Interview Practice • Be Discovered by Top Companies
                    </span>
                  </div>

                  {/* Main Heading */}
                  <h1 className="text-5xl md:text-6xl font-semibold tracking-tight text-gray-900 leading-[1.1] pt-2 mb-6">
                    Master your interviews
                    <br />
                    before they happen
                  </h1>

                  {/* Subheading */}
                  <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed mb-10">
                    Practice real interviews and produce performance signals employers can trust.
                    Dynamic role-specific simulations, and professional tools turn candidate preparation into measurable, actionable signals.
                  </p>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                    <Button
                      onClick={handleGetStarted}
                      size="lg"
                      className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 text-base rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 group"
                    >
                      Start Practicing Free
                      <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                    <Button
                      onClick={scrollToFeatures}
                      variant="outline"
                      size="lg"
                      className="border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-6 py-3 text-base rounded-lg"
                    >
                      Learn More
                    </Button>
                  </div>

                  {/* Trust Indicators */}
                  <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span>5x free interviews per month</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span>Dynamic feedback</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span>No credit card</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </motion.div>
        ) : (
          <motion.div
            key="recruiters"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {/* Recruiters Hero */}
            <section className="pt-8 pb-20 px-6">
              <div className="max-w-4xl mx-auto">
                <div className="text-center">
                  {/* Badge */}
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 border border-gray-200 mb-6">
                    <Target className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">
                      Data-Driven Talent Acquisition
                    </span>
                    <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700 border border-amber-200">Beta</span>
                  </div>

                  {/* Main Heading */}
                  <h1 className="text-5xl md:text-6xl font-semibold tracking-tight text-gray-900 leading-[1.1] pt-3 mb-6">
                    Discover top performers
                    <br />
                    before your competitors
                  </h1>

                  {/* Subheading */}
                  <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed mb-10">
                    OnSite produces interview performance signal that integrates directly into modern hiring workflows.
                    Find engineers, strategists, and analysts with verified skills and performance signals.
                  </p>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                    <Button
                      onClick={handleGetStarted}
                      size="lg"
                      className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 text-base rounded-lg shadow-sm hover:shadow-md transition-all duration-200 group"
                    >
                      Find Top Talent
                      <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                    <Button
                      onClick={scrollToFeatures}
                      variant="outline"
                      size="lg"
                      className="border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-6 py-3 text-base rounded-lg"
                    >
                      See Platform Features
                    </Button>
                  </div>

                  {/* Trust Indicators */}
                  <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span>Verified performance data</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span>Dynamic matching</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span>Market insights included</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dynamic Features Grid */}
      <AnimatePresence mode="wait">
        {activeAudience === 'candidates' ? (
          <motion.section
            key="candidates-features"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            id="features"
            className="px-6 py-24 bg-gray-50 border-y border-gray-200"
          >
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
                  Everything you need to succeed
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  OnSite provides comprehensive interview practice tailored to your career path,
                  from technical coding to strategic case analysis across 19 specialized roles.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-6">
                {/* Feature 1 - Multi-Industry Support */}
                <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-8 border border-blue-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center mb-6">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">19 Specialized Roles</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Dynamic interviews for Software Engineers, Data Scientists, Financial Analysts, Investment Bankers,
                    Consultants, and more. Each role gets industry-specific scenarios and questions.
                  </p>
                </div>

                {/* Feature 2 - OnSite Resume Review */}
                <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-8 border border-purple-100 hover:border-purple-200 hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 rounded-lg bg-purple-600 flex items-center justify-center mb-6">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">OnSite Resume Reviews</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Get intelligent resume analysis and writing assistance tailored to the jobs you want.
                    Align your application materials with target companies and roles.
                  </p>
                </div>

                {/* Feature 3 - Role Prep */}
                <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-8 border border-green-100 hover:border-green-200 hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 rounded-lg bg-green-600 flex items-center justify-center mb-6">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Role-Specific Prep</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Practice interviews tailored to specific companies and job postings. OnSite adapts questions
                    and scenarios based on the actual job description and requirements.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Feature 4 - Professional IDE */}
                <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-8 border border-orange-100 hover:border-orange-200 hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 rounded-lg bg-orange-600 flex items-center justify-center mb-6">
                    <Code2 className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Professional Code Editor</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Code in Python, JavaScript, Java, C++, Go, SQL, R, and more. Full syntax highlighting,
                    IntelliSense, auto-complete, and language-specific features powered by Monaco Editor.
                  </p>
                </div>

                {/* Feature 5 - Adaptive Interviewer */}
                <div className="bg-gradient-to-br from-pink-50 to-white rounded-xl p-8 border border-pink-100 hover:border-pink-200 hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 rounded-lg bg-pink-600 flex items-center justify-center mb-6">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Adaptive Interviewer</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Dynamic follow-up questions that adapt to your responses. The interviewer
                    simulates real conversations and probes deeper based on your answers.
                  </p>
                </div>

                {/* Feature 6 - Progress Tracking */}
                <div className="bg-gradient-to-br from-indigo-50 to-white rounded-xl p-8 border border-indigo-100 hover:border-indigo-200 hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 rounded-lg bg-indigo-600 flex items-center justify-center mb-6">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Detailed Analytics</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Track performance across multiple dimensions with detailed feedback categories.
                    Review complete interview history and monitor improvement over time.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>
        ) : (
          <motion.section
            key="recruiters-features"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            id="features"
            className="px-6 py-24 bg-gray-50 border-y border-gray-200"
          >
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
                  Find exceptional talent, faster
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  OnSite connects you with pre-vetted candidates backed by real performance data,
                  not just resumes. Make smarter hiring decisions with confidence.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mb-12">
                {/* Feature 1 */}
                <div className="bg-white rounded-lg p-8 border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200">
                  <div className="w-12 h-12 rounded-lg bg-gray-900 flex items-center justify-center mb-6">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Ranked Performance Signals</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Browse top-performing candidates ranked by assessment scores across engineering,
                    strategy, and analytics roles. See verified performance, not just claims.
                  </p>
                </div>

                {/* Feature 2 */}
                <div className="bg-white rounded-lg p-8 border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200">
                  <div className="w-12 h-12 rounded-lg bg-gray-900 flex items-center justify-center mb-6">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Smart Matching</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Dynamic candidate matching to your job requirements. Get ranked recommendations
                    based on skills, experience, performance trends, and role preparation activity.
                  </p>
                </div>

                {/* Feature 3 */}
                <div className="bg-white rounded-lg p-8 border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200">
                  <div className="w-12 h-12 rounded-lg bg-gray-900 flex items-center justify-center mb-6">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Market Insights</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Discover which companies candidates are preparing for, trending roles, and hiring
                    competition. Make strategic decisions with real market intelligence.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {/* Feature 4 */}
                <div className="bg-white rounded-lg p-8 border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200">
                  <div className="w-12 h-12 rounded-lg bg-gray-900 flex items-center justify-center mb-6">
                    <Briefcase className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Job Postings</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Post positions and get automatically matched with qualified candidates.
                    Track applications and manage your hiring pipeline in one place.
                  </p>
                </div>

                {/* Feature 5 */}
                <div className="bg-white rounded-lg p-8 border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200">
                  <div className="w-12 h-12 rounded-lg bg-gray-900 flex items-center justify-center mb-6">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Direct Messaging</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Contact top candidates directly with personalized outreach. Build relationships
                    with talent before your competitors even know they exist.
                  </p>
                </div>

                {/* Feature 6 */}
                <div className="bg-white rounded-lg p-8 border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200">
                  <div className="w-12 h-12 rounded-lg bg-gray-900 flex items-center justify-center mb-6">
                    <LineChart className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Performance Analytics</h3>
                  <p className="text-gray-600 leading-relaxed">
                    View detailed candidate performance metrics, improvement trends, and skill assessments.
                    Make data-driven hiring decisions with confidence.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Industries Section - Candidates Only */}
      {activeAudience === 'candidates' && (
        <section className="px-6 py-20 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
                Specialized for every career path
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                OnSite adapts to your industry and role, delivering interview experiences that
                match real-world scenarios you'll encounter.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mx-auto mb-4">
                  <Code2 className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Technology</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Software Engineer • Data Scientist • DevOps • Product Manager
                </p>
              </div>

              <div className="text-center p-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Finance</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Financial Analyst • Investment Banker • Quant • Accountant
                </p>
              </div>

              <div className="text-center p-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Consulting</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Strategy Consultant • Business Analyst • Operations Manager
                </p>
              </div>

              <div className="text-center p-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Marketing & Sales</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Marketing Manager • Sales Rep • Growth Marketer
                </p>
              </div>

              <div className="text-center p-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mx-auto mb-4">
                  <Layers className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Design</h3>
                <p className="text-sm text-gray-600 mb-3">
                  UX/UI Designer • Product Designer
                </p>
              </div>

              <div className="text-center p-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center mx-auto mb-4">
                  <GraduationCap className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Human Resources</h3>
                <p className="text-sm text-gray-600 mb-3">
                  HR Business Partner • Recruiter
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Get Discovered Section - Candidates Only */}
      {activeAudience === 'candidates' && (
        <section className="px-6 py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200 mb-6">
                <Award className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">
                  Stand Out to Employers
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
                Get discovered by top-tier companies
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Your verified performance on OnSite makes you visible to leading companies
                actively searching for performance signals. Turn your practice into opportunities.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <Target className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Verified Performance Data
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Unlike traditional resumes, your OnSite performance provides recruiters with
                      concrete evidence of your problem-solving abilities and technical skills.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      You Control Your Visibility
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Choose to remain anonymous or showcase your full profile. Opt-in when you're
                      ready to be discovered, and maintain full control over your privacy.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <Briefcase className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Direct Access to Opportunities
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Recruiters can reach out directly with relevant opportunities tailored
                      to your demonstrated skills and career goals.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1">
                    <TrendingUp className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Stand Out from the Crowd
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Be part of an elite talent pool where performance speaks louder than
                      credentials. Your results differentiate you from thousands of applicants.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-500 mb-4">
                Join OnSite to unlock recruiter visibility and career opportunities
              </p>
              <Button
                onClick={handleGetStarted}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-base rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
              >
                Get Started Free
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Dynamic How It Works Section */}
      <AnimatePresence mode="wait">
        {activeAudience === 'candidates' ? (
          <motion.section
            key="candidates-how"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="px-6 py-24"
          >
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
                  Simple, effective preparation
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Get started in minutes and begin practicing with dynamic interviews
                  tailored to your target role.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-12">
                <div className="relative text-center">
                  <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center font-semibold text-lg mb-5 text-white mx-auto">
                    1
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Your Focus</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Choose from engineering, strategy, or analyst tracks. Each path offers
                    role-specific scenarios and questions.
                  </p>
                </div>

                <div className="relative text-center">
                  <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center font-semibold text-lg mb-5 text-white mx-auto">
                    2
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Practice Naturally</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Respond via voice or text input. Your interviewer adapts to your answers
                    with intelligent follow-up questions.
                  </p>
                </div>

                <div className="relative text-center">
                  <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center font-semibold text-lg mb-5 text-white mx-auto">
                    3
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Get Detailed Insights</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Review comprehensive feedback on content, structure, and delivery.
                    Track your progress over time.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>
        ) : (
          <motion.section
            key="recruiters-how"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="px-6 py-24"
          >
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
                  Hire smarter, not harder
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  OnSite streamlines your hiring process with data-driven insights and
                  direct access to proven talent.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-12">
                <div className="relative text-center">
                  <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center font-semibold text-lg mb-5 text-white mx-auto">
                    1
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Browse Talent</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Explore leaderboards of top-performing candidates ranked by real assessment
                    data. Filter by role, skills, and performance metrics.
                  </p>
                </div>

                <div className="relative text-center">
                  <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center font-semibold text-lg mb-5 text-white mx-auto">
                    2
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Post & Match</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Create job postings and let OnSite match you with qualified candidates.
                    See compatibility scores based on verified performance.
                  </p>
                </div>

                <div className="relative text-center">
                  <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center font-semibold text-lg mb-5 text-white mx-auto">
                    3
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Connect & Hire</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Reach out directly to candidates, review detailed analytics, and make
                    confident hiring decisions backed by data.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Dynamic Final CTA */}
      <AnimatePresence mode="wait">
        {activeAudience === 'candidates' ? (
          <motion.section
            key="candidates-cta"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="px-6 py-20 bg-gray-50 border-t border-gray-200"
          >
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
                Ready to ace your next interview?
              </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                Join candidates using OnSite to build confidence and land their dream roles.
                Start practicing today—completely free.
              </p>
              <Button
                onClick={handleGetStarted}
                size="lg"
                className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-3 text-base rounded-lg shadow-sm hover:shadow-md transition-all duration-200 group"
              >
                Get Started Free
                <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </div>
          </motion.section>
        ) : (
          <motion.section
            key="recruiters-cta"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="px-6 py-20 bg-gray-50 border-t border-gray-200"
          >
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
                Ready to discover exceptional talent?
              </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                Join forward-thinking companies using OnSite to find, evaluate, and hire
                top performers. Access the platform today.
              </p>
              <Button
                onClick={handleGetStarted}
                size="lg"
                className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-3 text-base rounded-lg shadow-sm hover:shadow-md transition-all duration-200 group"
              >
                Get Started
                <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}